import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { StateService } from '../../../services/state/stateService';
import { State } from '../../../type/entities/state-type';

/*----------------------------------------------------------------------------------------
* EN: Hook to manage Contract states fetching and transformation logic.
* ES: Hook para gestionar la lógica de obtención y transformación de estados de contratos.
*----------------------------------------------------------------------------------------*/

const useContractStates = (initialFilters = {}) => {

    // HOOKS

    const { user } = useSelector((state: RootState) => state.auth);

    // STATES

    const [data, setData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [filters, setFilters] = useState<Record<string, any>>({
        limit: 99999,
        filter_filters: { ...initialFilters }
    });

    // FUNCTIONS

    // --------------------------------------------------------------
    /*
    * EN: Function to fetch contract states.
    * ES: Función para obtener estados de contratos.
    */
    // --------------------------------------------------------------
    const fetchContractStates = async () => {
        setIsLoading(true);
        try {
            const service = new StateService();
            let requestFilters = { ...filters };

            // IF USER IS NOT LINKED TO A DEPARTMENT

            if (user?.id) {
                if (!user.department) {
                    requestFilters = {
                        ...requestFilters,
                        filter_filters: {
                            ...requestFilters.filter_filters,
                            ignore_department_filter: true
                        }
                    };
                }
            }

            const response = await service.listStatusesForSelect(requestFilters);
            const fetchedData = response.getResponseData()?.data;
            
            if (fetchedData && fetchedData?.data) {
                const mappedData = Object.values(
                    fetchedData.data.reduce((acc: any, state: State) => {
                        if (!acc[state.id]) {
                            acc[state.id] = {
                                value: state.id,
                                label: state.name,
                                color: state.color,
                                statusOrder: state.statusOrder,
                                initialStatus: state.initialStatus,
                                draftStatus: state.draftStatus,
                                unsatisfactoryStatus: state.unsatisfactoryStatus,
                                requiresKoReason: state.requiresKoReason,
                                productTypes: [],
                            };
                        }

                        if (state.productTypes?.productType?.id) {
                            acc[state.id].productTypes.push(state.productTypes.productType.id);
                        }

                        return acc;
                    }, {})
                );

                setData(mappedData);
            }
        } catch (error) {
            console.log('Error fetching contract states:', error);
            setIsLoading(false);
        } finally {
            setIsLoading(false);
        }
    };
    // --------------------------------------------------------------

    // --------------------------------------------------------------
    /*
    * EN: Function to get the contract states list.
    * ES: Función para obtener la lista de estados de contratos.
    */
    // --------------------------------------------------------------
    const getContractStatesList = (entity?: string | null, notShowStatusID?: string | null) => {
        if (entity) return data.filter((state: any) => state.entity === entity);
        if (notShowStatusID) return data.filter((state: any) => state.value !== notShowStatusID);
        return data;
    };
    // --------------------------------------------------------------

    // --------------------------------------------------------------
    /*
    * EN: Function to add a filter to the contract states request.
    * ES: Función para agregar un filtro a la solicitud de estados de contratos.
    */
    // --------------------------------------------------------------
    const addContractStatesFilter = (key: string, value: any) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            filter_filters: {
                ...prevFilters.filter_filters,
                [key]: value
            }
        }));
    };
    // --------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        fetchContractStates();
    }, [filters]);

    // RETUTN

    return { fetchContractStates, getContractStatesList, addContractStatesFilter, isLoadingContractStates: isLoading };
}

export default useContractStates;